/**
 * useSetupIntent — create a Stripe SetupIntent for updating a payment method.
 *
 * in-app-checkout Phase 3 FIX B.
 *
 * Calls `POST /api/billing/setup-intent` which creates a Stripe SetupIntent
 * and returns its `client_secret`. The web side then renders `<SetupElement>`
 * (or `<PaymentElement>` in setup mode) + calls `stripe.confirmSetup()` to
 * tokenise the new card without charging it. On success, the confirmed
 * `payment_method` id is POSTed to `POST /api/billing/payment-method` to
 * attach it as the customer's default.
 *
 * Design:
 *   - `retry: 0` — setup mutations must not auto-retry (idempotency risk).
 *   - Import `SetupIntentResponse` from shared-types for the wire contract.
 *   - Envelope-unwrap pattern mirrors `usePaymentIntent`.
 */

import { useMutation } from '@tanstack/react-query';
import type { SetupIntentResponse } from '@arcanum-hq/shared-types';
import { api } from '@/lib/api';

interface SetupIntentEnvelope {
  data: SetupIntentResponse;
}

/**
 * useMutation hook for creating a Stripe SetupIntent (in-app card update).
 *
 * Usage:
 * ```tsx
 * const { mutateAsync: createSetupIntent, isPending } = useSetupIntent();
 * const { client_secret } = await createSetupIntent();
 * // → render <Elements clientSecret={client_secret}><PaymentElement/></Elements>
 * // → confirmSetup → POST payment_method_id to /api/billing/payment-method
 * ```
 */
export function useSetupIntent() {
  return useMutation<SetupIntentResponse, Error, void>({
    mutationFn: async () => {
      const envelope = await api.post<SetupIntentEnvelope>(
        '/api/billing/setup-intent',
        {}
      );
      return envelope.data;
    },
    // Never auto-retry setup mutations — idempotency risk.
    retry: 0,
  });
}
